/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import SignupPage from './pages/Signup';
import LoginPage from './pages/Login';
import NotFoundPage from './pages/NotFound';

const HomePage = lazy(() => import('./pages/Home'));
const SearchPage = lazy(() => import('./pages/Search'));
const MapPage = lazy(() => import('./pages/Map'));
const MessagesPage = lazy(() => import('./pages/Messages'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const StoreProfilePage = lazy(() => import('./pages/StoreProfile'));
const RetailerDashboard = lazy(() => import('./pages/RetailerDashboard'));
const ChatPage = lazy(() => import('./pages/Chat'));
const UserSettings = lazy(() => import('./pages/UserSettings'));
const SupportPage = lazy(() => import('./pages/Support'));
// Legal Phase A — public DPDP documents (lazy: react-markdown stays off the main chunk)
const LegalPrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const LegalTermsPage = lazy(() => import('./pages/legal/TermsPage'));
const LegalAccountDeletionPage = lazy(() => import('./pages/legal/AccountDeletionPage'));
const LegalGrievancePage = lazy(() => import('./pages/legal/GrievancePage'));
const LegalCookiesPage = lazy(() => import('./pages/legal/CookiesPage'));
import { HelmetProvider } from 'react-helmet-async';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { LocationProvider } from './context/LocationContext';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import Splash from './components/Splash';
import { useAuth } from './context/AuthContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useFcmRegistration } from './hooks/useFcmRegistration';
import ErrorBoundary from './components/ErrorBoundary';

import { useLocation } from 'react-router-dom';
import { isNative } from './lib/api';
import { isPublicPath } from './constants/publicRoutes';
import { Sentry } from './lib/sentry-frontend';

/**
 * Push deep-link guard (Session 128.26 hardening pass).
 *
 * Accepts:
 *   - relative paths (must start with a single "/", not "//" — that's
 *     protocol-relative which can resolve to a foreign origin)
 *   - same-origin absolute URLs (handles Capacitor's `https://localhost`)
 *   - `https://dukanchi.com/*` (cross-origin native → web bridge)
 *
 * Rejects: foreign origins, protocol-relative URLs, javascript:/data: URIs,
 * http: (downgrade) variants, and anything that fails URL parsing.
 */
function isSafeDeepLink(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  try {
    const u = new URL(url, window.location.origin);
    return (
      u.origin === window.location.origin ||
      (u.protocol === 'https:' && u.hostname === 'dukanchi.com')
    );
  } catch {
    return false;
  }
}


/**
 * BootGate — Session 123. Shows the Splash boot screen WHILE the initial auth
 * check (/api/auth/me) is in flight (AuthContext.isLoading), then reveals the
 * app. Gated on real hydration state — no fake timer; resolves via the auth
 * provider's `.finally`, so it always terminates.
 */
function BootGate({ children }: { children: ReactNode }) {
  const { isLoading } = useAuth();
  if (isLoading) return <Splash />;
  return <>{children}</>;
}

function FlowController() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  usePushNotifications();
  useFcmRegistration();        // native-only FCM token registration

  const isStandalone = (() => {
    try {
      if (localStorage.getItem('dk-browser-mode') === '1') return true;
    } catch(e) {
      // Session 128.26: lightweight breadcrumb so storage failures show up in
      // any later captured exception. Don't surface as a captured error — this
      // path is best-effort and falls through to the matchMedia check below.
      Sentry.addBreadcrumb({
        category: 'storage',
        level: 'warning',
        message: 'localStorage read failed (dk-browser-mode)',
        data: { error: String((e as Error)?.message ?? e) },
      });
    }

    const matchStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.indexOf('android-app://') === 0;

    if (matchStandalone) {
      // sessionStorage unavailable (private mode) — safe to ignore
      try { sessionStorage.setItem('dk-pwa', '1'); } catch(e) {}
      return true;
    }

    try {
      if (sessionStorage.getItem('dk-pwa') === '1') return true;
    } catch(e) {
      // Session 128.26: breadcrumb only — read failure is non-fatal; the
      // matchMedia path above is the authoritative standalone detector.
      Sentry.addBreadcrumb({
        category: 'storage',
        level: 'warning',
        message: 'sessionStorage read failed (dk-pwa)',
        data: { error: String((e as Error)?.message ?? e) },
      });
    }

    return false;
  })();

  useEffect(() => {
    // index.html handles browser→landing redirect before React loads
    if (!isStandalone) return;

    // PWA standalone flow — wait for auth to resolve
    if (isLoading) return;

    // Logged in → no redirect needed
    if (!!user) return;

    // Standalone + not logged in → go to /signup, unless on a public route
    // auth pages + /legal/* — DPDP docs must be readable pre-signup;
    // see src/constants/publicRoutes.ts)
    if (!isPublicPath(location.pathname)) {
      navigate('/landing', { replace: true });
    }
  }, [user, isLoading, location.pathname, navigate, isStandalone]);

  return null;
}

function BrowserModeBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const isBrowserMode = localStorage.getItem('dk-browser-mode') === '1';
      const isDismissed = sessionStorage.getItem('dk-banner-dismissed') === '1';
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      setShow(isBrowserMode && !isStandalone && !isDismissed);
    } catch(e) {
      // Session 128.26: banner is a discovery extra — if all three storage
      // reads fail, default to hidden (current behaviour) and breadcrumb the
      // event so the loss is visible in any later captured exception.
      Sentry.addBreadcrumb({
        category: 'storage',
        level: 'warning',
        message: 'BrowserModeBanner storage reads failed',
        data: { error: String((e as Error)?.message ?? e) },
      });
    }
  }, []);

  if (!show) return null;

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: '#FFF4ED', borderBottom: '1px solid #FFE4D6',
      padding: '8px 12px', display: 'flex',
      alignItems: 'center', justifyContent: 'space-between',
      fontSize: 12,
    }}>
      <span style={{ color: '#666' }}>💡 App install karein better experience ke liye</span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <a href="/landing" style={{ color: 'var(--b-orange)', fontWeight: 600, textDecoration: 'none' }}>Install</a>
        <button
          onClick={() => {
            // sessionStorage unavailable (private mode) — safe to ignore
            try { sessionStorage.setItem('dk-banner-dismissed', '1'); } catch(e) {}
            setShow(false);
          }}
          style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}
        >×</button>
      </div>
    </div>
  );
}

export default function App() {
  useEffect(() => {
    if (!isNative()) return;

    // Dynamic imports so web bundle stays clean — Capacitor plugins never ship to dukanchi.com
    (async () => {
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        const { App: CapApp } = await import('@capacitor/app');
        const { Keyboard } = await import('@capacitor/keyboard');

        // Status bar: light icons on deep-space dark background (futuristic v2)
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#060814' });

        // Keyboard: ensure inputs scroll into view on focus (default Android is jumpy)
        Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

        // Hardware back button: navigate browser history instead of exiting
        CapApp.addListener('backButton', ({ canGoBack }) => {
          if (canGoBack) {
            window.history.back();
          } else {
            CapApp.exitApp();
          }
        });

        // Push notification tap → navigate to the conversation (deep link)
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const url = action.notification.data?.url;
          if (url && typeof url === 'string') {
            // Session 128.26: deep-link guard. Push payloads originate
            // server-side but Sentry/triage history shows malformed/foreign
            // URLs sneak in (campaigns, spoofed pushes). Only navigate when
            // the URL is a relative path OR a same-origin/dukanchi.com
            // HTTPS URL — otherwise log a breadcrumb and drop it (the rare
            // legitimate cross-origin case can be handled by an in-app
            // <a target="_blank"> instead).
            if (isSafeDeepLink(url)) {
              window.location.href = url;
            } else {
              Sentry.addBreadcrumb({
                category: 'push',
                level: 'warning',
                message: 'push deep-link rejected (foreign origin)',
                data: { url: url.slice(0, 200) },
              });
            }
          }
        });

        // Hide splash AFTER the app shell mounts so user never sees a blank white frame
        await SplashScreen.hide({ fadeOutDuration: 200 });
      } catch (err) {
        console.error('[native-init] plugin setup failed:', err);
      }
    })();
  }, []);

  return (
    <ErrorBoundary>
    <HelmetProvider>
    <Router>
      <AuthProvider>
        <ToastProvider>
          <LocationProvider>
          <NotificationProvider>
            <FlowController />
            <BootGate>
            <div className="min-h-screen" style={{ background: 'var(--f-bg-deep)' }}>
              {import.meta.env.VITE_SHOW_PWA_PROMPTS === 'true' && <BrowserModeBanner />}
              <Suspense fallback={
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--f-bg-deep)' }}>
                  <div className="animate-spin" style={{ width: 36, height: 36, border: '3px solid var(--f-glass-border-2)', borderTopColor: 'var(--f-magenta)', borderRadius: '50%' }} />
                </div>
              }>
              <Routes>
                <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/store/:id" element={<StoreProfilePage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/retailer/dashboard" element={<ProtectedRoute><RetailerDashboard /></ProtectedRoute>} />
                <Route path="/chat/:userId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
                <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
                {/* Legal Phase A — public, no auth required */}
                <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
                <Route path="/legal/terms" element={<LegalTermsPage />} />
                <Route path="/legal/account-deletion" element={<LegalAccountDeletionPage />} />
                <Route path="/legal/grievance" element={<LegalGrievancePage />} />
                <Route path="/legal/cookies" element={<LegalCookiesPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              </Suspense>
              <BottomNav />
              {import.meta.env.VITE_SHOW_PWA_PROMPTS === 'true' && <PWAInstallPrompt />}
            </div>
            </BootGate>
          </NotificationProvider>
          </LocationProvider>
        </ToastProvider>
      </AuthProvider>
    </Router>
    </HelmetProvider>
    </ErrorBoundary>
  );
}
